import {Component, computed, effect, inject, OnInit, untracked} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {SharedModule} from 'primeng/api';
import {FormArray, FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {CountryService} from '@frontend/shared';
import {BaseStepComponent} from '../../../../../shared/components/wizard/base-step.component';
import {CompanyWizardService} from '../company-wizard.service';
import {CreateCompanyCountryRequest, CreateUpdateCompanyRequest} from '@frontend/shared-api';
import {TranslocoDirective} from '@jsverse/transloco';
import {CountryCode} from '@frontend/directus';
import {validateEntities} from '../../../../../shared/utils/array-utils';
import {CountryStepData} from '../company-wizard.types';
import {Tooltip} from 'primeng/tooltip';
import {CountrySelectComponent} from '../../../../../shared/components/inputs/country-select/country-select.component';

@Component({
  selector: 'app-company-wizard-country-step',
  imports: [SharedModule, ReactiveFormsModule, TranslocoDirective, Tooltip, CountrySelectComponent],
  templateUrl: './company-wizard-country-step.component.html',
})
export class CompanyWizardCountryStepComponent
  extends BaseStepComponent<CreateUpdateCompanyRequest>
  implements OnInit
{
  override readonly stepIndex = 2;

  private readonly fb = inject(FormBuilder);
  readonly countryService = inject(CountryService);
  private readonly companyWizardService = inject(CompanyWizardService);

  readonly form = this.fb.group({
    countries: this.fb.array<FormControl<string | null>>([
      this.fb.control<string | null>(null, Validators.required),
    ]),
  });

  protected get countrySelectionControls(): FormArray<FormControl<string | null>> {
    return this.form.get('countries') as FormArray<FormControl<string | null>>;
  }

  private readonly formStatusSignal = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  private readonly formValueSignal = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  protected readonly isValidComputed = computed(() => {
    const formStatus = this.formStatusSignal();
    return formStatus === 'VALID';
  });

  protected readonly selectedCountryDetails = computed((): CountryCode[] => {
    const allCountries = this.countryService.allCountries();
    const formValue = this.formValueSignal();
    const selectedCodes =
      formValue.countries?.filter((code): code is string => code !== null) ?? [];

    return selectedCodes
      .map((code) => allCountries.find((country) => country.isoCode === code))
      .filter((country): country is CountryCode => !!country);
  });

  protected readonly areAllCountriesCitiesValid = computed((): boolean => {
    const formData = this.companyWizardService.formData;
    const selectedCountries = this.selectedCountryDetails();
    if (!formData.countries) return false;
    return selectedCountries.every((country) => {
      const formCountry = formData.countries?.find(
        (fc: CreateCompanyCountryRequest) => fc.countryCode === country.isoCode,
      );
      return (
        !!formCountry &&
        formCountry.cities.length > 0 &&
        validateEntities(formCountry.cities, 'name')
      );
    });
  });

  protected readonly officesAreValid = computed((): boolean => {
    const formData = this.companyWizardService.formData;
    if (!formData.countries) return false;
    return formData.countries.every((country) => {
      if (!country.cities || country.cities.length === 0) {
        return false;
      }
      return country.cities.every((city) => {
        if (!city.offices || city.offices.length === 0) {
          return false;
        }
        return validateEntities(city.offices, 'name') && validateEntities(city.offices, 'address');
      });
    });
  });

  constructor() {
    super();

    effect(() => {
      const countriesCount = this.countryService.allCountries().length;

      if (countriesCount > 0) {
        this.updateStepData();

        const allCitiesValid = this.areAllCountriesCitiesValid();
        const officesValid = this.officesAreValid();

        untracked(() => {
          this.companyWizardService.updateStepValidity({
            stepIndex: 3,
            isValid: allCitiesValid,
          });

          this.companyWizardService.updateStepValidity({
            stepIndex: 4,
            isValid: officesValid,
          });
        });
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeFormData();
  }

  addCountryDropdown(): void {
    this.countrySelectionControls.push(this.fb.control<string | null>(null, Validators.required));
  }

  removeCountryDropdown(index: number): void {
    this.countrySelectionControls.removeAt(index);
  }

  getExcludedCountryCodes(index: number): string[] {
    return this.countrySelectionControls.value.filter(
      (code, i): code is string => i !== index && code !== null,
    );
  }

  private initializeFormData(): void {
    const countryData = this.companyWizardService.getStepData<CountryStepData>(this.stepIndex);
    if (countryData?.countries && countryData.countries.length > 0) {
      this.countrySelectionControls.clear();

      countryData.countries.forEach((country) => {
        this.countrySelectionControls.push(
          this.fb.control<string | null>(country.countryCode, Validators.required),
        );
      });
    }
  }

  private updateStepData(): void {
    const selectedDetails = this.selectedCountryDetails();
    const currentStepData = this.companyWizardService.getStepData<CountryStepData>(this.stepIndex);
    const currentCountries = currentStepData?.countries || [];

    const countries = selectedDetails.map((country) => {
      const existingCountry = currentCountries.find((c) => c.countryCode === country.isoCode);
      return {
        countryCode: country.isoCode,
        isDeletable: existingCountry?.isDeletable ?? true,
      };
    });

    const hasChanged = this.hasCountryDataChanged(countries, currentCountries);

    if (hasChanged) {
      this.companyWizardService.updateStepData({
        stepIndex: this.stepIndex,
        data: {countries},
      });
    }
  }

  private hasCountryDataChanged(
    newCountries: Array<{countryCode: string; isDeletable: boolean}>,
    currentCountries: Array<{countryCode: string; isDeletable?: boolean}>,
  ): boolean {
    if (newCountries.length !== currentCountries.length) {
      return true;
    }

    for (let i = 0; i < newCountries.length; i++) {
      const newCountry = newCountries[i];
      const currentCountry = currentCountries[i];
      if (
        newCountry.countryCode !== currentCountry?.countryCode ||
        newCountry.isDeletable !== (currentCountry?.isDeletable ?? true)
      ) {
        return true;
      }
    }

    return false;
  }

  isCountryDeletable(index: number): boolean {
    const countryCode = this.countrySelectionControls.value[index];
    const countryData = this.companyWizardService.getStepData<CountryStepData>(this.stepIndex);

    if (!countryData?.countries) {
      return true;
    }

    const country = countryData.countries.find((c) => c.countryCode === countryCode);
    return country?.isDeletable ?? true;
  }
}
